import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Checkbox } from '../../../ui/checkbox';
import { Badge } from '../../../ui/badge';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Textarea } from '../../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import {
  Download,
  Eye,
  CheckCircle2,
  XCircle,
  FileText,
  Search,
  Users,
  BadgeCheck,
  AlertCircle,
  Activity,
  Filter,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileUser,
  Tickets,
} from 'lucide-react';
import { ActivityLogsPage } from './ActivityLogsPage';
import { getStatusHistory, getVisaCompeletedList, getVisaCompeletedSummary } from '../../../services/visaService';
import { FullScreenLoading } from '../../../components/ui/loader';
import useDebounce from '../../../hooks/useDebounce';
import Pagination from '../../../ui/pagination';
import { NoApplicantFound } from '../../../ui/noRecordfound';
import { DraftVisaItem } from '../../../types/itsDetails';
import { StatusHistory } from '../../../types/visa';
import { useNavigate } from 'react-router-dom';
import { getFileViewUrl } from '../../../utils/commonutils';
import { usePageHeader } from "contexts/PageHeaderContext";

interface Applicant {
  id: string;
  visaId: string;
  tourRefNo: string;
  partnerName: string;
  officeName: string;
  applicantName: string;
  applicantITS: string;
  passportNo: string;
  nationality: string;
  visaRequired: boolean;
  visaFee?: number;
  status: 'Approved';
  completedDate: string;
}

interface CompletedListPageProps {
  onNavigate?: (page: string) => void;
  onViewApplicant?: (applicantId: string) => void;
  onShowActivityLogs?: (applicantId: string) => void;
}


interface VisaCompleteSummaryResponse {
  totalApplications: number;
  visaRequired: number;
  noVisaRequired: number;
}

// Mock data for completed applications - all approved
const mockCompletedApplicants: Applicant[] = [
  {
    id: 'app1',
    visaId: 'VISA-2025001',
    tourRefNo: 'NKERP/TOUR/2025/4580',
    partnerName: 'Al-Mustafa Tours',
    officeName: 'Mumbai Main',
    applicantName: 'Mulla Mohammed Ahmed Khan',
    applicantITS: '12345678',
    passportNo: 'J1234567',
    nationality: 'India',
    visaRequired: true,
    visaFee: 120.00,
    status: 'Approved',
    completedDate: '22-01-2025'
  },
  {
    id: 'app2',
    visaId: 'VISA-2025002',
    tourRefNo: 'NKERP/TOUR/2025/4580',
    partnerName: 'Al-Mustafa Tours',
    officeName: 'Mumbai Main',
    applicantName: 'Mulla Hamza Juzer Lunavadwala',
    applicantITS: '30325631',
    passportNo: 'V9828249',
    nationality: 'India',
    visaRequired: true,
    visaFee: 120.00,
    status: 'Approved',
    completedDate: '22-01-2025'
  },
  {
    id: 'app3',
    visaId: 'VISA-2025003',
    tourRefNo: 'NKERP/TOUR/2025/4581',
    partnerName: 'Zainab Travels',
    officeName: 'Delhi Office',
    applicantName: 'Sheikh Abdullah Rahman',
    applicantITS: '20456789',
    passportNo: 'K8765432',
    nationality: 'India',
    visaRequired: false,
    status: 'Approved',
    completedDate: '22-01-2025'
  },
  {
    id: 'app4',
    visaId: 'VISA-2025004',
    tourRefNo: 'NKERP/TOUR/2025/4581',
    partnerName: 'Zainab Travels',
    officeName: 'Delhi Office',
    applicantName: 'Fatima Hassan Ali',
    applicantITS: '30567890',
    passportNo: 'L9876543',
    nationality: 'India',
    visaRequired: true,
    visaFee: 150.00,
    status: 'Approved',
    completedDate: '22-01-2025'
  },
  {
    id: 'app5',
    visaId: 'VISA-2025005',
    tourRefNo: 'NKERP/TOUR/2025/4582',
    partnerName: 'Hussein Tours',
    officeName: 'Lucknow Branch',
    applicantName: 'Ali Raza Khan',
    applicantITS: '40678901',
    passportNo: 'M1234890',
    nationality: 'India',
    visaRequired: true,
    visaFee: 120.00,
    status: 'Approved',
    completedDate: '22-01-2025'
  },
  {
    id: 'app6',
    visaId: 'VISA-2025006',
    tourRefNo: 'NKERP/TOUR/2025/4583',
    partnerName: 'Karbalai Tours',
    officeName: 'Bangalore Office',
    applicantName: 'Zahra Fatima Hussain',
    applicantITS: '50789012',
    passportNo: 'N2345901',
    nationality: 'India',
    visaRequired: true,
    visaFee: 150.00,
    status: 'Approved',
    completedDate: '21-01-2025'
  },
  {
    id: 'app7',
    visaId: 'VISA-2025007',
    tourRefNo: 'NKERP/TOUR/2025/4583',
    partnerName: 'Karbalai Tours',
    officeName: 'Bangalore Office',
    applicantName: 'Hussain Ali Akbar',
    applicantITS: '60890123',
    passportNo: 'O3456012',
    nationality: 'India',
    visaRequired: false,
    status: 'Approved',
    completedDate: '21-01-2025'
  }
];

export const CompletedListPage = ({ onNavigate, onViewApplicant, onShowActivityLogs }: CompletedListPageProps) => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [applicants] = useState(mockCompletedApplicants);
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);
  const [, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [, setCurrentRejectId] = useState<string | null>(null);
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [selectedApplicantForLogs, setSelectedApplicantForLogs] = useState<StatusHistory[]>([]);
  const [statushisLoader, setStatusHistoryLoader] = useState<boolean>(false)


  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [dashboardSummary, setDashboardSummary] = useState<VisaCompleteSummaryResponse | null>(null)
  const [totalVisaCompeletedCount, setTotalVisaCompeletedCount] = useState(0)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    applicantName: '',
    itsNo: '',
    passportNo: '',
    nationality: '',
    partnerName: '',
    officeName: '',
    visaId: '',
    tourRefNo: '',
    visaRequired: '',
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(() => ({
    ...filters,
    searchTerm,
  }), [filters, searchTerm]);
  const debouncedFilters = useDebounce(mergedFilters, 400);

  const [visaCompeletedList, setVisaCompeletedList] = useState<DraftVisaItem[]>(
    [],
  );


  // API CALLS STARTS
  const fetchCompeletedSummary = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVisaCompeletedSummary()
      setDashboardSummary(response)
    } catch (error) {
      console.log("Failed to fetch visa compelete summary")
    } finally {
      setLoading(false)
    }
  }, [])


  useEffect(() => {
    setPageHeader({
      title: "Completed Applications",
      icon: <Tickets className="h-4 w-4" />,
      onBack: () => navigate("/visa-muwafiqa"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  useEffect(() => {
    fetchCompeletedSummary()
  }, [fetchCompeletedSummary])


  const fetchVisaCompeletedList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVisaCompeletedList(debouncedFilters);
      setVisaCompeletedList(data.items);
      setTotalVisaCompeletedCount(data.totalCount);
    } catch {
      console.log("Failed to fetch visa compelete list")
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);


  useEffect(() => {
    fetchVisaCompeletedList();
  }, [fetchVisaCompeletedList]);
  // API CALLS ENDS


  const filteredApplicants = applicants.filter(app => {
    // Search term filter (global search)
    const matchesSearch = searchTerm === '' ||
      app.visaId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.tourRefNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.partnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.officeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.applicantITS.includes(searchTerm) ||
      app.passportNo.toLowerCase().includes(searchTerm.toLowerCase());

    // Advanced filters
    const matchesApplicantName = !filters.applicantName ||
      app.applicantName.toLowerCase().includes(filters.applicantName.toLowerCase());

    const matchesITS = !filters.itsNo ||
      app.applicantITS.includes(filters.itsNo);

    const matchesPassport = !filters.passportNo ||
      app.passportNo.toLowerCase().includes(filters.passportNo.toLowerCase());

    const matchesNationality = !filters.nationality ||
      app.nationality.toLowerCase().includes(filters.nationality.toLowerCase());

    const matchesPartner = !filters.partnerName ||
      app.partnerName.toLowerCase().includes(filters.partnerName.toLowerCase());

    const matchesOffice = !filters.officeName ||
      app.officeName.toLowerCase().includes(filters.officeName.toLowerCase());

    const matchesVisaId = !filters.visaId ||
      app.visaId.toLowerCase().includes(filters.visaId.toLowerCase());

    const matchesTourRef = !filters.tourRefNo ||
      app.tourRefNo.toLowerCase().includes(filters.tourRefNo.toLowerCase());

    const matchesVisaRequired = filters.visaRequired === 'all' ||
      (filters.visaRequired === 'yes' && app.visaRequired) ||
      (filters.visaRequired === 'no' && !app.visaRequired);

    return matchesSearch &&
      matchesApplicantName &&
      matchesITS &&
      matchesPassport &&
      matchesNationality &&
      matchesPartner &&
      matchesOffice &&
      matchesVisaId &&
      matchesTourRef &&
      matchesVisaRequired;
  });

  // const totalApplicants = filteredApplicants.length;
  // const visaRequiredCount = filteredApplicants.filter(app => app.visaRequired).length;
  // const noVisaCount = filteredApplicants.filter(app => !app.visaRequired).length;

  //   const handleSelectAll = (checked: boolean) => {
  //     setSelectAll(checked);
  //     if (checked) {
  //       // Only select visa required applicants
  //       setSelectedApplicants(filteredApplicants.filter(app => app.visaRequired).map(app => app.id));
  //     } else {
  //       setSelectedApplicants([]);
  //     }
  //   };


  const handleSelectApplicant = (applicantId: string) => {
    if (selectedApplicants.includes(applicantId)) {
      setSelectedApplicants(
        selectedApplicants.filter((id) => id !== applicantId),
      );
    } else {
      setSelectedApplicants([...selectedApplicants, applicantId]);
    }
  };


  //   const handleRejectSelected = () => {
  //     if (selectedApplicants.length === 0) return;
  //     setCurrentRejectId(null); // Batch rejection
  //     setShowRejectDialog(true);
  //   };

  //   const handleRejectSingle = (applicantId: string) => {
  //     const applicant = applicants.find(app => app.id === applicantId);
  //     if (!applicant?.visaRequired) return;

  //     setCurrentRejectId(applicantId);
  //     setSelectedApplicants([applicantId]);
  //     setShowRejectDialog(true);
  //   };

  const handleConfirmReject = () => {
    const finalReason = rejectReason === 'Other' ? customReason : rejectReason;
    console.log('Rejecting applicants:', selectedApplicants, 'Reason:', finalReason);

    // Reset and close
    setShowRejectDialog(false);
    setRejectReason('');
    setCustomReason('');
    setSelectedApplicants([]);
    setSelectAll(false);
    setCurrentRejectId(null);
  };


  const handleExport = () => {
    console.log('Exporting applicants:', selectedApplicants.length > 0 ? selectedApplicants : filteredApplicants.map(a => a.id));
  };

  // const handleViewApplicant = (applicantId: string) => {
  //   // Navigate to detail view page
  //   if (onNavigate) {
  //     onNavigate('visa-muwafiqa/completed/view');
  //   }
  // };


  const handleShowActivityLogs = async (visaApplicantId: number) => {
    try {
      setStatusHistoryLoader(true)
      const data = await getStatusHistory(visaApplicantId);
      if (data?.length > 0) {
        setSelectedApplicantForLogs(data);
        setShowActivityLogs(true);
      }
    } catch (error) {
      console.error("Error fetching status history:", error);
    } finally {
      setStatusHistoryLoader(false)
    }
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedApplicants = useMemo(() => {
    if (!sortColumn) return visaCompeletedList;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return [...visaCompeletedList].sort((a: any, b: any) => {
      const aValue = String(a[sortColumn] ?? "");
      const bValue = String(b[sortColumn] ?? "");

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = String(aValue).localeCompare(
        String(bValue),
        undefined,
        { numeric: true, sensitivity: "base" }
      );

      return sortDirection === "asc"
        ? comparison
        : -comparison;
    });
  }, [visaCompeletedList, sortColumn, sortDirection]);



  return (
    <div className="min-h-screen">
      {loading && <FullScreenLoading />}
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200" style={{ marginTop: "0px" }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">Total Applications</p>
                <p className="text-2xl text-gray-900">{dashboardSummary?.totalApplications ?? 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">Visa Required</p>
                <p className="text-2xl text-gray-900">{dashboardSummary?.visaRequired ?? 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <BadgeCheck className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">No Visa Required</p>
                <p className="text-2xl text-gray-900">{dashboardSummary?.noVisaRequired ?? 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Bar with Filter */}
      <div className="flex gap-2" style={{ marginTop: "10px" }}>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by Visa ID, Tour Ref, Partner, Office, Name, ITS, or Passport..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setFilters(prev => ({
                ...prev,
                pageNo: 1,  // Reset to first page on search
              }))
            }}
            className="pl-10 md:pl-10 border-gray-200 rounded-lg h-9 md:h-10 text-sm"
          />
          {searchTerm && <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchTerm("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-500"
          >
            <X className="w-4 h-4" />
          </Button>}
        </div>
        <Button
          variant={showFilters ? "default" : "outline"}
          size="icon"
          onClick={() => setShowFilters(!showFilters)}
          style={showFilters ? { background: '#5B9BD5' } : {}}
          className={showFilters ? "text-white" : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"}
          title="Toggle Advanced Filters"
        >
          <Filter className="w-5 h-5" />
        </Button>
      </div>

      {/* Advanced Filters Section */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50 mb-4" style={{ gap: "0px" }}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter completed applications by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Applicant Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-applicant-name" className="text-sm">Applicant Name</Label>
                <Input
                  id="filter-applicant-name"
                  placeholder="Search by name..."
                  value={filters.applicantName}
                  onChange={(e) => setFilters(prev => ({ ...prev, applicantName: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* ITS Number Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-its" className="text-sm">ITS Number</Label>
                <Input
                  id="filter-its"
                  placeholder="Search by ITS..."
                  value={filters.itsNo}
                  onChange={(e) => setFilters(prev => ({ ...prev, itsNo: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Passport Number Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-passport" className="text-sm">Passport No.</Label>
                <Input
                  id="filter-passport"
                  placeholder="Search by passport..."
                  value={filters.passportNo}
                  onChange={(e) => setFilters(prev => ({ ...prev, passportNo: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Nationality Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-nationality" className="text-sm">Nationality</Label>
                <Input
                  id="filter-nationality"
                  placeholder="Search by nationality..."
                  value={filters.nationality}
                  onChange={(e) => setFilters(prev => ({ ...prev, nationality: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Partner Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-partner" className="text-sm">Partner Name</Label>
                <Input
                  id="filter-partner"
                  placeholder="Search by partner..."
                  value={filters.partnerName}
                  onChange={(e) => setFilters(prev => ({ ...prev, partnerName: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Office Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-office" className="text-sm">Office Name</Label>
                <Input
                  id="filter-office"
                  placeholder="Search by office..."
                  value={filters.officeName}
                  onChange={(e) => setFilters(prev => ({ ...prev, officeName: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Visa ID Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-visa-id" className="text-sm">Visa ID</Label>
                <Input
                  id="filter-visa-id"
                  placeholder="Search by Visa ID..."
                  value={filters.visaId}
                  onChange={(e) => setFilters(prev => ({ ...prev, visaId: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Tour Ref No Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-tour-ref" className="text-sm">Tour Reference</Label>
                <Input
                  id="filter-tour-ref"
                  placeholder="Search by tour ref..."
                  value={filters.tourRefNo}
                  onChange={(e) => setFilters(prev => ({ ...prev, tourRefNo: e.target.value, pageNo: 1 }))}
                  className="h-9 text-sm"
                />
              </div>

              {/* Visa Required Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-visa-required" className="text-sm">Visa Required</Label>
                <Select value={filters.visaRequired || 'all'}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, visaRequired: value === 'all' ? '' : value, pageNo: 1 }))}>
                  <SelectTrigger id="filter-visa-required" className="h-9 text-sm">
                    <SelectValue placeholder="Select visa requirement..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Summary & Clear Button */}
            {(filters.applicantName || filters.itsNo || filters.passportNo || filters.nationality || filters.visaRequired) && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg mt-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {/* {filters.applicationNo && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Application: {filters.applicationNo}
                      </Badge>
                    )} */}
                    {filters.applicantName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Name: {filters.applicantName}
                      </Badge>
                    )}
                    {filters.itsNo && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        ITS: {filters.itsNo}
                      </Badge>
                    )}
                    {filters.passportNo && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Passport: {filters.passportNo}
                      </Badge>
                    )}
                    {filters.nationality && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Nationality: {filters.nationality}
                      </Badge>
                    )}
                    {/* {filters.status && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Status: {filters.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Badge>
                    )} */}
                    {filters.visaRequired && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Visa Required: {filters.visaRequired === 'true' ? 'Yes' : 'No'}
                      </Badge>
                    )}
                  </div>
                </div>
                {/* <div className="flex justify-end gap-2 pt-2"> */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({
                    applicantName: '',
                    itsNo: '',
                    passportNo: '',
                    nationality: '',
                    partnerName: '',
                    officeName: '',
                    visaId: '',
                    tourRefNo: '',
                    visaRequired: '',
                    pageNo: 1,
                    pageSize: 20,
                  })}
                  className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                >
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Card */}
      <Card className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-md">
        <CardContent className="p-4">
          {/* Header Bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <p className="text-xs text-gray-700">
                Showing {filteredApplicants.length} application{filteredApplicants.length !== 1 ? 's' : ''}
              </p>
              {selectedApplicants.length > 0 && (
                <Badge style={{ backgroundColor: '#5B9BD5' }} className="text-white">
                  {selectedApplicants.length} selected
                </Badge>
              )}
            </div>

            <div className="flex gap-2">
              {selectedApplicants.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleExport}
                  className="gap-2 h-9 text-xs"
                  size="sm"
                  style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}
                >
                  <Download className="w-3.5 h-3.5" />
                  Export Selected ({selectedApplicants.length})
                </Button>
              )}
            </div>
          </div>

          {/* Table - Desktop View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#E3F2FD' }}>
                  <th className="px-4 py-3 text-center text-xs w-12" style={{ color: '#5B9BD5' }}>
                    <Checkbox
                      checked={selectedApplicants.length ===
                        visaCompeletedList.length &&
                        visaCompeletedList.length > 0
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedApplicants(visaCompeletedList.map(app => String(app.visaApplicationId)));
                        } else {
                          setSelectedApplicants([]);
                        }
                      }}
                      aria-label="Select all"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('firstName')}
                  >
                    <div className="flex items-center gap-1">
                      Applicant Details
                      {sortColumn === 'firstName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('passportNo')}
                  >
                    <div className="flex items-center gap-1">
                      Passport No.
                      {sortColumn === 'passportNo' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                   <th
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('passportNationality')}
                  >
                    <div className="flex items-center gap-1">
                      Passport Nationality
                      {sortColumn === 'passportNationality' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-xs" style={{ color: '#5B9BD5' }}>
                    Visa Fee
                  </th>
                  <th
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('toOfficeName')}
                  >
                    <div className="flex items-center gap-1">
                      Partner / Office
                      {sortColumn === 'toOfficeName' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('applicationNumber')}
                  >
                    <div className="flex items-center gap-1">
                      Tour Info
                      {sortColumn === 'applicationNumber' ? (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUpDown className="w-3 h-3 opacity-30" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-xs" style={{ color: '#5B9BD5' }}>
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs text-right" style={{ color: '#5B9BD5' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visaCompeletedList && sortedApplicants.map((applicant) => (
                  <tr
                    key={applicant.visaApplicationId}
                    className="hover:bg-sky-50/30 transition-colors"
                  >
                    <td className="px-3 py-2.5 text-center">
                      <Checkbox
                        checked={selectedApplicants.includes(String(applicant.visaApplicationId))}
                        onCheckedChange={(checked) => handleSelectApplicant(String(applicant.visaApplicationId))}
                        id={`select-${applicant.visaApplicationId}`}
                        disabled={!applicant.visaRequired}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <p className="text-xs text-gray-900">
                            {applicant.title} {applicant.firstName}{" "}
                            {applicant.middleName} {applicant.lastName}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {/* {applicant.itsNumber || applicant.loginId} */}
                            {applicant.itsNo || "N/A"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-xs text-gray-900">{applicant.passportNo}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="text-[10px] text-gray-500">{applicant.passportNationality}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      {applicant.visaRequired ? (
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 w-fit text-[10px] px-1.5 py-0">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                            Yes
                          </Badge>
                          <div className="flex items-center gap-1 text-xs font-semibold text-gray-900">
                            ${applicant.visaFee ? applicant.visaFee : 0}
                            <FileText className="w-2.5 h-2.5 text-gray-400" />
                          </div>
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-[10px] px-1.5 py-0">
                          No
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {/* <p className="text-xs text-gray-900">{applicant.partnerName}</p> */}
                      <p className="text-[10px] text-gray-500">{applicant.toOfficeName || ''}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold" style={{ color: '#5B9BD5' }}>
                          {applicant.applicationNumber}
                        </p>
                        {/* <p className="text-[10px] text-gray-500">Linked to:</p> */}
                        {/* <p className="text-[10px] text-gray-900">{applicant.tourRefNo}</p> */}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0">
                        <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                        Approved
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1">


                        {applicant.certificateFilePath !== null && <div className="flex items-center justify-center gap-1">
                          <button
                            className="flex items-center justify-center  h-7 w-7 p-0 hover:bg-blue-50"
                            onClick={() => {
                              const fullUrl = getFileViewUrl(applicant?.certificateFilePath);
                              window.open(fullUrl, "_blank");
                            }}
                            title={"view document"}
                          >
                            <FileUser
                              className="w-4 h-4 cursor-pointer text-blue-600"
                            />
                          </button>
                        </div>}

                        <Button
                          size="sm"
                          variant="ghost"
                          // onClick={() => handleViewApplicant(String(applicant.visaApplicationId))}
                          onClick={() =>
                            navigate(
                              `/visa-muwafiqa/visa-document-required/${applicant.visaApplicationId}`,
                            )
                          }
                          className="h-7 w-7 p-0 hover:bg-blue-50"
                          style={{ color: '#5B9BD5' }}
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleShowActivityLogs(applicant?.visaApplicationId)}
                          className="h-7 w-7 p-0 hover:bg-purple-50"
                          style={{ color: '#9333ea' }}
                          title="Activity Logs"
                        >
                          <Activity className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {visaCompeletedList.map((applicant) => (
              <Card
                key={applicant.visaApplicationId}
                className="border-gray-200 hover:border-[#5B9BD5]/50 transition-all"
              >
                <CardContent className="p-4">
                  {/* Header Row with Checkbox and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedApplicants.includes(String(applicant.visaApplicationId))}
                        onCheckedChange={(checked) => handleSelectApplicant(String(applicant.visaApplicationId))}
                        id={`mobile-select-${applicant.id}`}
                        disabled={!applicant.visaRequired}
                        className="mt-1"
                      />
                      <div>
                        <h3 className="font-semibold text-sm text-gray-900 mb-1">
                          {applicant.title} {applicant.firstName}{" "}
                          {applicant.middleName} {applicant.lastName}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                            ITS: {applicant.itsNo}
                          </Badge>
                          <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                            Approved
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Applicant Details Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                    {/* Passport */}
                    <div>
                      <p className="text-gray-500 text-[10px] mb-0.5">Passport No.</p>
                      <p className="text-gray-900 font-medium">{applicant.passportNo}</p>
                      <p className="text-gray-500 text-[10px]">{applicant.nationality}</p>
                    </div>

                    {/* Visa Fee */}
                    <div>
                      <p className="text-gray-500 text-[10px] mb-0.5">Visa Fee</p>
                      {applicant.visaRequired ? (
                        <div className="space-y-1">
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 w-fit text-[10px] px-1.5 py-0">
                            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />
                            Yes
                          </Badge>
                          <div className="flex items-center gap-1 text-xs font-semibold text-gray-900">
                            ${applicant.visaFee ? applicant.visaFee : 0}
                            <FileText className="w-2.5 h-2.5 text-gray-400" />
                          </div>
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-[10px] px-1.5 py-0">
                          No
                        </Badge>
                      )}
                    </div>

                    {/* Partner */}
                    <div>
                      <p className="text-gray-500 text-[10px] mb-0.5">Partner</p>
                      {/* <p className="text-gray-900 font-medium">{applicant.partnerName}</p> */}
                      <p className="text-gray-500 text-[10px]">{applicant.toOfficeName || ""}</p>
                    </div>

                    {/* Visa ID */}
                    <div>
                      <p className="text-gray-500 text-[10px] mb-0.5">Visa ID</p>
                      <p className="font-semibold text-xs" style={{ color: '#5B9BD5' }}>
                        {applicant.applicationNumber}
                      </p>
                    </div>
                  </div>

                  {/* Tour Reference - Full Width */}
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    {/* <p className="text-gray-500 text-[10px] mb-0.5">Tour Reference</p> */}
                    {/* <p className="text-gray-900 text-xs">{applicant.tourRefNo}</p> */}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">

                    {applicant.certificateFilePath !== null && <div className="flex items-center justify-center gap-1">
                      <button
                        className="flex items-center justify-center  h-7 w-7 p-0 hover:bg-blue-50"
                        onClick={() => {
                          const fullUrl = getFileViewUrl(applicant?.certificateFilePath);
                          window.open(fullUrl, "_blank");
                        }}
                        title={"view document"}
                      >
                        <FileUser
                          className="w-4 h-4 cursor-pointer text-blue-600"
                        />
                      </button>
                    </div>}

                    <Button
                      size="sm"
                      variant="outline"
                      // onClick={() => handleViewApplicant(String(applicant.visaApplicationId))}
                      onClick={() =>
                        navigate(
                          `/visa-muwafiqa/visa-document-required/${applicant.visaApplicationId}`,
                        )
                      }
                      className="flex-1 h-8 text-xs border-[#5B9BD5] hover:bg-sky-50"
                      style={{ color: '#5B9BD5' }}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      View Details
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShowActivityLogs(applicant?.visaApplicationId)}
                      className="flex-1 h-8 text-xs border-purple-500 text-purple-600 hover:bg-purple-50"
                    >
                      <Activity className="w-3.5 h-3.5 mr-1" />
                      Activity Logs
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {visaCompeletedList.length === 0 && (
            <NoApplicantFound />
          )}

          <Pagination
            totalItems={totalVisaCompeletedCount}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters(prev => ({
                ...prev,
                pageNo: page, // convert back to 0-based
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1, // reset page
              }))
            }
            label=""
          />
        </CardContent>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Reject Visa Application{selectedApplicants.length > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              {selectedApplicants.length > 1
                ? `You are about to reject ${selectedApplicants.length} visa applications.`
                : 'You are about to reject this visa application.'
              } This action cannot be undone. Please provide a reason.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Rejection Reason</Label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger id="reason">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Immigration Failed">Immigration Failed</SelectItem>
                  <SelectItem value="Tickets Missed">Tickets Missed</SelectItem>
                  <SelectItem value="Document Issues">Document Issues</SelectItem>
                  <SelectItem value="Medical Reasons">Medical Reasons</SelectItem>
                  <SelectItem value="Visa Denied">Visa Denied by Embassy</SelectItem>
                  <SelectItem value="Payment Issues">Payment Issues</SelectItem>
                  <SelectItem value="Applicant Withdrew">Applicant Withdrew</SelectItem>
                  <SelectItem value="Other">Other (Specify)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {rejectReason === 'Other' && (
              <div className="space-y-2">
                <Label htmlFor="custom-reason">Please Specify</Label>
                <Textarea
                  id="custom-reason"
                  placeholder="Enter the reason for rejection..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectReason('');
                setCustomReason('');
                setCurrentRejectId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmReject}
              disabled={!rejectReason || (rejectReason === 'Other' && !customReason.trim())}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Logs Dialog */}
      {selectedApplicantForLogs && (
        <ActivityLogsPage
          visaId={selectedApplicantForLogs[0]?.visaApplicationId}
          statushisLoader={statushisLoader}
          selectedApplicantForLogs={selectedApplicantForLogs}
          open={showActivityLogs}
          onClose={() => {
            setShowActivityLogs(false);
            setSelectedApplicantForLogs([]);
          }}
        />
      )}
    </div>
  );
};